// src/pages/Invoices/Invoices.jsx

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, where } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../context/AuthContext';
import InvoiceDetailModal from './InvoiceDetailModal';
import toast from 'react-hot-toast';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    headerLeft: {
        display: 'flex',
        flexDirection: 'column'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    subtitle: {
        fontSize: '16px',
        color: '#64748b',
        marginTop: '4px'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        textDecoration: 'none',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    table: {
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
        background: 'white',
        borderRadius: '12px',
        overflow: 'hidden'
    },
    th: {
        background: '#f8fafc',
        padding: '16px 20px',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
        fontSize: '12px',
        color: '#374151',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    td: {
        padding: '16px 20px',
        borderBottom: '1px solid #f1f5f9',
        fontSize: '14px',
        color: '#374151'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    trHoverActive: {
        background: '#f8fafc'
    },
    emptyState: {
        textAlign: 'center',
        padding: '64px 32px',
        color: '#64748b'
    },
    emptyIcon: {
        fontSize: '48px',
        marginBottom: '16px'
    },
    emptyText: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px'
    },
    emptySubtext: {
        fontSize: '14px',
        color: '#64748b'
    },
    loadingState: {
        textAlign: 'center',
        padding: '48px',
        color: '#64748b',
        fontSize: '16px'
    },
    statusBadge: {
        padding: '4px 12px',
        borderRadius: '8px',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    statusCompleted: {
        background: '#dcfce7',
        color: '#166534'
    },
    statusPending: {
        background: '#fef3c7',
        color: '#92400e'
    },
    statusDraft: {
        background: '#f3f4f6',
        color: '#374151'
    }
};

function Invoices() {
    const { userData, canViewPrices } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [sendingInvoiceId, setSendingInvoiceId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'sale', 'return'

    useEffect(() => {
        if (!userData?.companyId) return;
        const companyId = userData.companyId;

        const q = query(collection(db, 'companies', companyId, 'invoices'), orderBy('invoiceDate', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        const qPayments = query(collection(db, 'companies', companyId, 'transactions'), where('type', '==', 'Tahsilat'));
        const unsubPayments = onSnapshot(qPayments, (snapshot) => {
            setPayments(snapshot.docs.map(doc => doc.data()));
        });

        return () => { unsubscribe(); unsubPayments(); };
    }, [userData]);

    const invoicesWithProgress = useMemo(() => {
        const paidMap = {};
        payments.forEach(p => {
            const targetId = p.invoiceId || p.orderId;
            if (targetId) {
                paidMap[targetId] = (paidMap[targetId] || 0) + (p.amount || 0);
            }
        });

        return invoices.map(inv => {
            const paidAmount = paidMap[inv.id] || (inv.orderId ? paidMap[inv.orderId] : 0) || 0;
            const totalAmount = inv.total || 0;
            return {
                ...inv,
                paidAmount,
                paymentPercentage: totalAmount > 0 ? Math.min(100, (paidAmount / totalAmount) * 100) : 0
            };
        });
    }, [invoices, payments]);

    const filteredInvoices = useMemo(() => {
        let result = invoicesWithProgress;

        // Type filter
        if (typeFilter !== 'all') {
            result = result.filter(inv => {
                if (typeFilter === 'return') return inv.type === 'return';
                if (typeFilter === 'sale') return inv.type !== 'return';
                return true;
            });
        }

        // Date filter
        if (dateFilter !== 'all') {
            const now = new Date();
            let cutoff;
            if (dateFilter === 'thisMonth') {
                cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
            } else if (dateFilter === 'last3Months') {
                cutoff = new Date(now.getFullYear(), now.getMonth() - 3, 1);
            }
            if (cutoff) {
                result = result.filter(inv => {
                    const d = inv.invoiceDate?.seconds ? new Date(inv.invoiceDate.seconds * 1000) : null;
                    return d && d >= cutoff;
                });
            }
        }

        // Status filter
        if (statusFilter !== 'all') {
            const now = new Date();
            result = result.filter(inv => {
                if (statusFilter === 'paid') return (inv.paymentPercentage || 0) >= 100;
                if (statusFilter === 'unpaid') return (inv.paymentPercentage || 0) < 100;
                if (statusFilter === 'overdue') {
                    const dueDate = inv.dueDate?.seconds ? new Date(inv.dueDate.seconds * 1000) : null;
                    return dueDate && dueDate < now && (inv.paymentPercentage || 0) < 100;
                }
                return true;
            });
        }

        // Search filter
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            result = result.filter(inv => 
                (inv.id || '').toLowerCase().includes(lowerSearch) ||
                (inv.invoiceNumber || '').toLowerCase().includes(lowerSearch) ||
                (inv.customerName || '').toLowerCase().includes(lowerSearch)
            );
        }

        return result;
    }, [invoicesWithProgress, searchTerm, dateFilter, statusFilter, typeFilter]);

    const getStatusStyle = (status, paymentPercentage) => {
        if (paymentPercentage >= 100) return { ...styles.statusBadge, ...styles.statusCompleted };
        
        switch (status?.toLowerCase()) {
            case 'tamamlandı':
            case 'complete':
                return { ...styles.statusBadge, ...styles.statusCompleted };
            case 'beklemede':
            case 'pending':
                return { ...styles.statusBadge, ...styles.statusPending };
            default:
                return { ...styles.statusBadge, ...styles.statusDraft };
        }
    };

    const getEInvoiceStatusBadge = (invoice) => {
        const status = invoice.eInvoiceStatus;
        const type = invoice.eInvoiceType === 'EARSIV' ? 'E-Arşiv' : 'E-Fatura';
        if (!status) return <span style={{ ...styles.statusBadge, background: '#f1f5f9', color: '#64748b', fontSize: '11px' }}>—</span>;
        switch (status) {
            case 'sent':
            case 'approved':
                return <span style={{ ...styles.statusBadge, background: '#dcfce7', color: '#166534' }}>✅ {type}</span>;
            case 'sending':
                return <span style={{ ...styles.statusBadge, background: '#dbeafe', color: '#1e40af' }}>⏳ Gönderiliyor</span>;
            case 'error':
                return (
                    <span style={{ ...styles.statusBadge, background: '#fef2f2', color: '#dc2626', cursor: 'pointer' }} title={invoice.eInvoiceError || 'Hata'}>❌ Hata</span>
                );
            default:
                return <span style={{ ...styles.statusBadge, background: '#f1f5f9', color: '#64748b' }}>{status}</span>;
        }
    };

    const handleSendEInvoice = async (e, invoice) => {
        e.stopPropagation();
        if (sendingInvoiceId) return;
        if (!window.confirm(`"${invoice.customerName}" için ${invoice.eInvoiceType === 'EARSIV' || !invoice.customerName ? 'E-Arşiv' : 'E-Fatura'} kesmek istediğinize emin misiniz?`)) return;
        
        setSendingInvoiceId(invoice.id);
        try {
            const sendFn = httpsCallable(functions, 'sendEInvoice');
            const result = await sendFn({
                companyId: userData.companyId,
                invoiceId: invoice.id
            });
            if (result.data.success) {
                toast.success(result.data.message);
            } else {
                toast.error('Hata: ' + (result.data.error || 'Bilinmeyen hata'));
            }
        } catch (err) {
            toast.error('E-Fatura gönderilemedi: ' + err.message);
        }
        setSendingInvoiceId(null);
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <h1 style={styles.title}>Faturalar ve Siparişler</h1>
                    <p style={styles.subtitle}>Satış faturalarını ve siparişleri yönetin</p>
                </div>
                <Link to="/faturalar/yeni" style={styles.button}>
                    + Yeni Fatura Oluştur
                </Link>
            </div>

            <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: '15px' }}>
                    <Link to="/faturalar/satis" style={{ ...styles.button, background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                        Satış Faturaları
                    </Link>
                    <Link to="/alis-faturalari" style={{ ...styles.button, background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                        Alış Faturaları
                    </Link>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Tarih Filtresi */}
                    <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', padding: '4px', borderRadius: '8px' }}>
                        {[{ key: 'all', label: 'Tümü' }, { key: 'thisMonth', label: 'Bu Ay' }, { key: 'last3Months', label: 'Son 3 Ay' }].map(f => (
                            <button key={f.key} onClick={() => setDateFilter(f.key)}
                                style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                                    background: dateFilter === f.key ? 'white' : 'transparent',
                                    color: dateFilter === f.key ? '#06C167' : '#64748b',
                                    boxShadow: dateFilter === f.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                                }}>{f.label}</button>
                        ))}
                    </div>
                    {/* Durum Filtresi */}
                    <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', padding: '4px', borderRadius: '8px' }}>
                        {[{ key: 'all', label: 'Tüm Durum' }, { key: 'unpaid', label: 'Ödenmemiş' }, { key: 'overdue', label: 'Gecikmiş' }, { key: 'paid', label: 'Ödenmiş' }].map(f => (
                            <button key={f.key} onClick={() => setStatusFilter(f.key)}
                                style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                                    background: statusFilter === f.key ? 'white' : 'transparent',
                                    color: statusFilter === f.key ? (f.key === 'overdue' ? '#dc2626' : f.key === 'paid' ? '#10b981' : '#06C167') : '#64748b',
                                    boxShadow: statusFilter === f.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                                }}>{f.label}</button>
                        ))}
                    </div>
                    {/* Tür Filtresi */}
                    <div style={{ display: 'flex', gap: '4px', background: '#f1f5f9', padding: '4px', borderRadius: '8px' }}>
                        {[{ key: 'all', label: 'Tümü' }, { key: 'sale', label: 'Satış' }, { key: 'return', label: 'İade' }].map(f => (
                            <button key={f.key} onClick={() => setTypeFilter(f.key)}
                                style={{ padding: '6px 12px', borderRadius: '6px', border: 'none', fontSize: '12px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                                    background: typeFilter === f.key ? 'white' : 'transparent',
                                    color: typeFilter === f.key ? (f.key === 'return' ? '#dc2626' : '#06C167') : '#64748b',
                                    boxShadow: typeFilter === f.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                                }}>{f.label}</button>
                        ))}
                    </div>
                    {/* Arama */}
                    <input 
                        type="text" 
                        placeholder="Fatura No veya Müşteri Ara..." 
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #e2e8f0', width: '220px', fontSize: '13px', outline: 'none' }}
                    />
                </div>
            </div>

            <div style={styles.box}>
                {loading ? (
                    <div style={styles.loadingState}>
                        Yükleniyor...
                    </div>
                ) : invoices.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>📊</div>
                        <div style={styles.emptyText}>Henüz fatura oluşturulmamış</div>
                        <div style={styles.emptySubtext}>İlk faturatı oluşturmak için yukarıdaki butonu kullanın</div>
                    </div>
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Fatura No</th>
                                <th style={styles.th}>Müşteri</th>
                                <th style={styles.th}>Tarih</th>
                                <th style={styles.th}>Vade Tarihi</th>
                                <th style={styles.th}>Tutar</th>
                                <th style={styles.th}>Ödeme</th>
                                <th style={styles.th}>Durum</th>
                                <th style={styles.th}>E-Fatura</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredInvoices.map(invoice => (
                                <tr
                                    key={invoice.id}
                                    style={{ ...styles.trHover, ...(hoveredRow === invoice.id ? styles.trHoverActive : {}) }}
                                    onMouseEnter={() => setHoveredRow(invoice.id)}
                                    onMouseLeave={() => setHoveredRow(null)}
                                    onClick={() => setSelectedInvoice(invoice)}
                                >
                                    <td style={styles.td}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            {invoice.type === 'return' && <span style={{ background: '#fef2f2', color: '#dc2626', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '700' }}>İADE</span>}
                                            {invoice.invoiceNumber || invoice.id.substring(0, 6)}
                                        </div>
                                    </td>
                                    <td style={styles.td}>{invoice.customerName}</td>
                                    <td style={styles.td}>{invoice.invoiceDate?.seconds ? new Date(invoice.invoiceDate.seconds * 1000).toLocaleDateString('tr-TR') : '-'}</td>
                                    <td style={{
                                        ...styles.td,
                                        color: (invoice.dueDate && invoice.status !== 'Tamamlandı' && new Date(invoice.dueDate.seconds * 1000) < new Date()) ? '#dc2626' : '#374151',
                                        fontWeight: (invoice.dueDate && invoice.status !== 'Tamamlandı' && new Date(invoice.dueDate.seconds * 1000) < new Date()) ? '600' : 'normal'
                                    }}>
                                        {invoice.dueDate?.seconds ? new Date(invoice.dueDate.seconds * 1000).toLocaleDateString('tr-TR') : '-'}
                                        {(invoice.dueDate && invoice.status !== 'Tamamlandı' && new Date(invoice.dueDate.seconds * 1000) < new Date()) &&
                                            <span style={{ fontSize: '11px', marginLeft: '6px', background: '#fee2e2', padding: '2px 6px', borderRadius: '4px' }}>Gecikmiş</span>
                                        }
                                    </td>
                                    <td style={{ ...styles.td, color: invoice.type === 'return' ? '#dc2626' : '#374151', fontWeight: invoice.type === 'return' ? '600' : 'normal' }}>
                                        {canViewPrices ? `${invoice.type === 'return' ? '' : ''}${(invoice.total || 0).toFixed(2)} ₺` : '***'}
                                    </td>
                                    <td style={styles.td}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '80px' }}>
                                            <div style={{ fontSize: '11px', fontWeight: '600', color: (invoice.paymentPercentage || 0) >= 100 ? '#10b981' : '#06C167' }}>
                                                %{(invoice.paymentPercentage || 0).toFixed(0)}
                                            </div>
                                            <div style={{ width: '100%', height: '4px', background: '#f1f5f9', borderRadius: '2px', overflow: 'hidden' }}>
                                                <div style={{ width: `${invoice.paymentPercentage || 0}%`, height: '100%', background: (invoice.paymentPercentage || 0) >= 100 ? '#10b981' : '#06C167', transition: 'width 0.3s ease' }} />
                                            </div>
                                        </div>
                                    </td>
                                    <td style={styles.td}>
                                        <span style={getStatusStyle(invoice.status, invoice.paymentPercentage)}>
                                            {invoice.paymentPercentage >= 100 ? 'ÖDENDİ' : (invoice.status || 'Taslak')}
                                        </span>
                                    </td>
                                    <td style={{ ...styles.td, whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                                        {invoice.eInvoiceStatus === 'sent' || invoice.eInvoiceStatus === 'approved' ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                {getEInvoiceStatusBadge(invoice)}
                                                {invoice.gibInvoiceNo && <span style={{ fontSize: '11px', color: '#64748b' }}>{invoice.gibInvoiceNo}</span>}
                                            </div>
                                        ) : invoice.eInvoiceStatus === 'error' ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {getEInvoiceStatusBadge(invoice)}
                                                <button
                                                    onClick={(e) => handleSendEInvoice(e, invoice)}
                                                    disabled={sendingInvoiceId === invoice.id}
                                                    style={{ background: 'none', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}
                                                >Tekrar Dene</button>
                                            </div>
                                        ) : invoice.eInvoiceStatus === 'sending' ? (
                                            getEInvoiceStatusBadge(invoice)
                                        ) : (
                                            <button
                                                onClick={(e) => handleSendEInvoice(e, invoice)}
                                                disabled={sendingInvoiceId === invoice.id}
                                                style={{
                                                    background: sendingInvoiceId === invoice.id ? '#94a3b8' : 'linear-gradient(135deg, #0ea5e9, #0284c7)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    padding: '6px 12px',
                                                    fontSize: '12px',
                                                    fontWeight: '600',
                                                    cursor: sendingInvoiceId === invoice.id ? 'not-allowed' : 'pointer',
                                                    whiteSpace: 'nowrap',
                                                    boxShadow: '0 2px 4px rgba(14,165,233,0.2)'
                                                }}
                                            >
                                                {sendingInvoiceId === invoice.id ? '⏳ ...' : '🧾 E-Fatura Kes'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {selectedInvoice && (
                <InvoiceDetailModal 
                    invoice={selectedInvoice} 
                    onClose={() => setSelectedInvoice(null)} 
                    canViewPrices={canViewPrices} 
                />
            )}
        </div>
    );
}

export default Invoices;
